import { isNotNullOrUndefined, isNullOrUndefined } from './NullOrUndefined.js';

/**
 * Inspired by {@link https://github.com/kozhevnikov/proxymise}.
 */
export namespace ProxyChain {
  export const Wrapped = Symbol('ProxyChain.Wrapped');

  export function create<TTarget extends MaybePromise<Value> | undefined>(
    target: TTarget
  ) {
    if (typeof target === 'object') {
      const proxy = () => target;
      proxy[Wrapped] = true as const;

      return new Proxy<{
        (): TTarget & object;
        [Wrapped]: true;
      }>(proxy, handler);
    }

    return typeof target === 'function'
      ? new Proxy<typeof target>(target, handler)
      : target;
  }

  export const handler: ProxyHandler<object> = {
    construct<TInstance extends object>(
      target:
        | {
            new (...args: ReadonlyArray<unknown>): TInstance;
            [Wrapped]?: never;
          }
        | {
            (): new (...args: ReadonlyArray<unknown>) => TInstance;
            [Wrapped]: true;
          },
      argumentsList: ReadonlyArray<unknown>
    ) {
      if (target[Wrapped]) target = target();

      return create(Reflect.construct(target, argumentsList));
    },

    get<
      TTarget extends Record<PropertyKey, Value>,
      TProperty extends keyof TTarget
    >(
      target:
        | (MaybePromise<TTarget> & { [Wrapped]?: never })
        | {
            (): MaybePromise<TTarget>;
            [Wrapped]: true;
          },
      property: TProperty,
      receiver: unknown
    ) {
      if (target[Wrapped]) target = target();

      if (
        property !== 'then' &&
        property !== 'catch' &&
        isNotNullOrUndefined(target) &&
        typeof target.then === 'function'
      ) {
        return create(target.then((value) => get(value, property, receiver)));
      }

      return create(get(target as TTarget, property, receiver));
    },

    apply<TResult extends Value, TArgs extends ReadonlyArray<unknown>>(
      target:
        | (MaybePromise<(...args: TArgs) => TResult> & {
            [Wrapped]?: never;
          })
        | {
            (): MaybePromise<(...args: TArgs) => TResult>;
            [Wrapped]: true;
          },
      thisArg: unknown,
      argumentsList: TArgs
    ) {
      if (target[Wrapped]) target = target();

      if (typeof target.then === 'function') {
        return create(
          target.then((value) => apply(value, thisArg, argumentsList))
        );
      }

      return create(apply(target, thisArg, argumentsList));
    }
  };

  function get<
    TTarget extends NonNullable<unknown>,
    TProperty extends keyof TTarget
  >(
    target: TTarget | null | undefined,
    property: TProperty,
    receiver: unknown
  ): TTarget[TProperty] | undefined {
    if (isNullOrUndefined(target)) return undefined;

    const value =
      typeof target === 'object'
        ? Reflect.get(target, property, receiver)
        : target[property];

    if (typeof value === 'function' && typeof value.bind === 'function') {
      return Object.assign(value.bind(target) as typeof value, value);
    }

    return value;
  }

  function apply<TResult extends Value, TArgs extends ReadonlyArray<unknown>>(
    target: ((...args: TArgs) => TResult) | null | undefined,
    thisArg: unknown,
    argumentsList: TArgs
  ): TResult | undefined {
    if (isNullOrUndefined(target)) return undefined;

    return Reflect.apply(target, thisArg, argumentsList);
  }

  export type MaybePromise<T> = Promise<T> | (T & { readonly then?: never });

  export type Value =
    | object
    | ((...args: ReadonlyArray<unknown>) => unknown)
    | undefined;
}
